import { Component, ErrorInfo, ReactNode } from "react";
import ChartErrorState from "./ChartErrorState";

interface ChartErrorBoundaryProps {
  children: ReactNode;
}

interface ChartErrorBoundaryState {
  hasError: boolean;
}

/**
 * Stops one chart's render crash from unmounting the rest of the page.
 *
 * Nothing else in this app does this (no `componentDidCatch` existed anywhere
 * before this file — see the region-map crash this was added for: a malformed
 * `/cpi/regions` payload threw inside `EmissionsByRegionMap`, and with no
 * boundary above it React unmounts the entire tree, taking every other chart
 * on the page down with it).
 *
 * Deliberately generic and scoped to wherever it is placed, not to the whole
 * page: the point is that ONE chart failing must not hide the others. A class
 * component is required here — React 17 has no hook equivalent of
 * `componentDidCatch`.
 */
class ChartErrorBoundary extends Component<
  ChartErrorBoundaryProps,
  ChartErrorBoundaryState
> {
  state: ChartErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ChartErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("ChartErrorBoundary caught an error", error, info);
  }

  /**
   * Lets the fallback's "Retry" button attempt a fresh render of the children
   * rather than being permanently stuck. If the underlying data has not
   * changed, the same crash simply recurs and the boundary catches it again.
   */
  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return <ChartErrorState onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}

export default ChartErrorBoundary;
